import { RefObject, useEffect } from 'react';
import { isMobileViewport } from './viewport';

/*
 * Keeps a full-screen popup's composer above the on-screen keyboard.
 * Writes the keyboard height (px) to `--kbd-inset` on the given element while a
 * mobile keyboard is open. The popup container should size with:
 *   height: calc(100dvh - var(--kbd-inset, 0px));
 *
 * Uses the Visual Viewport API (no-op where unavailable, e.g. desktop).
 */

type ElementTarget = HTMLElement | null;
type Target = ElementTarget | (() => ElementTarget);

// Plain attach helper for class components. Returns a cleanup function.
// `target` may be an element or a getter `() => element | null` — the getter form
// handles popups whose root is conditionally rendered (resolved on each event).
export function attachKeyboardInset(target: Target): () => void {
  if (!target || typeof window === 'undefined') return () => {};
  const vv = window.visualViewport;
  if (!vv) return () => {};
  const getEl = (): ElementTarget => (typeof target === 'function' ? target() : target);
  const apply = () => {
    const el = getEl();
    if (!el) return;
    if (!isMobileViewport()) {
      el.style.removeProperty('--kbd-inset');
      return;
    }
    const inset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
    el.style.setProperty('--kbd-inset', `${inset}px`);
  };
  apply();
  vv.addEventListener('resize', apply);
  vv.addEventListener('scroll', apply);
  return () => {
    vv.removeEventListener('resize', apply);
    vv.removeEventListener('scroll', apply);
    const el = getEl();
    if (el) el.style.removeProperty('--kbd-inset');
  };
}

// Hook form for functional components.
export function useKeyboardInset(ref: RefObject<HTMLElement>, active = true): void {
  useEffect(() => {
    if (!active || !ref || !ref.current) return undefined;
    return attachKeyboardInset(ref.current);
  }, [ref, active]);
}
